import { observable } from 'mobx';
import moment from 'moment';
import type { Moment } from '../lib/moment';
import type { ChangeFeeOriginalMappingResponseArgs } from './change_fee_original_mapping2';
import ChangeFeeOriginalMapping, {
  convertChangeFeeOriginalMappingResponseToArgs
} from './change_fee_original_mapping2';

export type ChangeFeeElementResponseArgs = {
  id: number;
  content: string;
  canceled_at?: string;
  start_at?: string;
  end_at?: string;
  change_fee_original_mappings?: ChangeFeeOriginalMappingResponseArgs[];
};

export type ChangeFeeElementArgs = {
  id: number;
  content: string;
  canceledAt?: Moment;
  startAt?: Moment;
  endAt?: Moment;
  mappings?: ChangeFeeOriginalMapping[];
};

export const convertChangeFeeElementResponseToArgs = (response: ChangeFeeElementResponseArgs) => {
  const args: ChangeFeeElementArgs = {
    id: response.id,
    content: response.content,
    canceledAt: response.canceled_at ? moment(response.canceled_at) : undefined,
    startAt: response.start_at ? moment(response.start_at).startOf('day') : undefined,
    endAt: response.end_at ? moment(response.end_at).endOf('day') : undefined,
    mappings: response.change_fee_original_mappings
      ? response.change_fee_original_mappings.map(
          m => new ChangeFeeOriginalMapping(convertChangeFeeOriginalMappingResponseToArgs(m))
        )
      : []
  };
  return args;
};

export default class ChangeFeeElement {
  @observable id: number;
  @observable content: string;
  @observable canceledAt?: Moment;
  @observable startAt?: Moment;
  @observable endAt?: Moment;
  @observable mappings: ChangeFeeOriginalMapping[];

  constructor(args: ChangeFeeElementArgs) {
    this.id = args.id;
    this.content = args.content;
    this.canceledAt = args.canceledAt;
    this.startAt = args.startAt;
    this.endAt = args.endAt;
    this.mappings = args.mappings || [];
  }

  get type() {
    return 'change_fee';
  }

  get title() {
    return '変更手数料';
  }

  get summary() {
    return this.content;
  }

  get name() {
    return this.title;
  }

  detail() {
    return '';
  }

  optionalAllowanceItems(): ChangeFeeOriginalMapping[] {
    return this.mappings.filter(m => m.originalTraceId);
  }

  addMapping(): void {
    this.mappings.push(new ChangeFeeOriginalMapping({}));
  }

  removeMapping(index: number): void {
    if (index !== -1) {
      this.mappings.splice(index, 1);
    }
  }

  validationErrors(_orderItemStatus?: number): string[] {
    const errors: string[] = [];

    if (!this.content || this.content === '') {
      errors.push('変更手数料の内容を入力してください。');
    }

    if (this.optionalAllowanceItems().length < 1) {
      errors.push('変更手数料対象を選択してください。');
    }

    return errors;
  }

  get updateParams() {
    return {
      type: this.type,
      content: this.content,
      canceled_at: this.canceledAt?.format('YYYY-MM-DD'),
      start_at: this.startAt?.format('YYYY-MM-DD HH:mm'),
      end_at: this.endAt?.format('YYYY-MM-DD HH:mm'),
      change_fee_original_mappings_attributes: this.optionalAllowanceItems().map(m => m.updateParams)
    };
  }

  get description(): string {
    return `<変更手数料>
■内容
${this.content || ''}
■変更日
${this.canceledAt?.format('YYYY/MM/DD')}`;
  }

  get startTime() {
    return this.startAt;
  }

  get endTime() {
    return this.endAt;
  }
}
